'use client';

import { useEffect, useMemo, useState } from 'react';
import { 
  Chart as ChartJS, 
  CategoryScale, 
  LinearScale, 
  PointElement, 
  LineElement, 
  BarElement,
  BarController,
  LineController,
  Title, 
  Tooltip, 
  Legend, 
  ChartOptions,
  ChartData,
  ScriptableContext,
  defaults,
  TooltipItem,
} from 'chart.js';
import { Chart } from 'react-chartjs-2';
import annotationPlugin from 'chartjs-plugin-annotation';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import {
  ScreenerType,
  SCREENER_SMA_WINDOWS,
  meetsSmaGate,
} from '@/lib/screenerCriteria';

// Register ChartJS components
ChartJS.register(
  CategoryScale, 
  LinearScale, 
  PointElement, 
  LineElement, 
  BarElement,
  BarController,
  LineController,
  Title, 
  Tooltip, 
  Legend,
  annotationPlugin,
  ChartDataLabels
);

// Configure global Chart.js defaults
defaults.font.family = '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif';
defaults.color = '#666';
defaults.animation = false;

interface StockCombinedChartProps {
  dailyData: {
    date: string;
    close_price: number;
    dma_20?: number | null;
    dma_50?: number | null;
    dma_200: number;
    avg30_tq?: number | null;
    traded_qty: number;
    traded_amount: number;
    delivery_qty: number;
    delivery_amount: number;
    number_of_trades?: number;
    avg_trade_worth?: number;
    meets_criteria?: boolean;
    exceeds_max_criteria?: boolean;
  }[];
  stockName: string;
  compact?: boolean;
  isin?: string;
  onChartClick?: (isin: string) => void;
  deliveryThreshold: number;
  minDeliveryAmountThreshold: number;
  maxDeliveryAmountThreshold: number;
  showTooltip?: boolean;
  barPercentage?: number;
  /**
   * Active screener — controls the SMA-comparison gate used to decide
   * which days qualify, and which SMA reference lines are drawn.
   */
  screener?: ScreenerType;
}

export default function StockCombinedChart({
  dailyData,
  stockName,
  compact = false,
  isin,
  onChartClick,
  deliveryThreshold,
  minDeliveryAmountThreshold,
  maxDeliveryAmountThreshold,
  showTooltip = true,
  barPercentage = 0.6,
  screener = 'big_buy',
}: StockCombinedChartProps) {
  // Provide defaults if thresholds are not passed
  const effectiveMinDeliveryAmountThreshold = minDeliveryAmountThreshold ?? 0;
  const effectiveMaxDeliveryAmountThreshold = maxDeliveryAmountThreshold ?? Number.MAX_SAFE_INTEGER;
  
  const [chartTheme, setChartTheme] = useState<'light' | 'dark'>('light');
  
  useEffect(() => {
    // Check if dark mode is enabled
    const isDarkMode = document.documentElement.classList.contains('dark');
    setChartTheme(isDarkMode ? 'dark' : 'light');
    
    // Listen for theme changes
    const observer = new MutationObserver(() => {
      const isDark = document.documentElement.classList.contains('dark');
      setChartTheme(isDark ? 'dark' : 'light');
    });
    
    observer.observe(document.documentElement, { 
      attributes: true, 
      attributeFilter: ['class'] 
    });
    
    return () => observer.disconnect();
  }, []);

  // Validate and clean the data before processing
  const sortedData = useMemo(() => {
    // Filter out any invalid entries before sorting
    const validData = [...(dailyData || [])]
      .filter(d => d && d.date) // Ensure we have valid data points with dates
      .map(d => {
        // Calculate traded amount from traded_qty and close_price if not already provided
        const calculatedTradedAmount = d.traded_amount || (d.traded_qty * d.close_price);
        
        // Calculate delivery amount from delivery_qty and close_price if not already available
        const calculatedDeliveryAmount = d.delivery_amount || (d.delivery_qty * d.close_price);
        
        // Calculate delivery percentage
        const deliveryPct = d.delivery_qty / d.traded_qty * 100;
        
        // Calculate average trade worth
        const avgTradeWorth = d.number_of_trades && d.number_of_trades > 0
          ? (d.close_price * d.traded_qty) / d.number_of_trades
          : 0;
        
        // Determine if meets basic criteria for the active screener.
        // The SMA-comparison gate switches based on `screener` so this
        // chart highlights bars consistently with the table the user
        // came from.
        const passesSmaGate = meetsSmaGate(
          {
            close_price: d.close_price,
            traded_qty: d.traded_qty,
            delivery_qty: d.delivery_qty,
            dma_20: d.dma_20,
            dma_50: d.dma_50,
            dma_200: d.dma_200,
            avg30_tq: d.avg30_tq,
          },
          screener,
        );
        const meetsBasicCriteria = passesSmaGate && deliveryPct >= deliveryThreshold;
        
        // Check if meets all criteria within the min-max range
        const meetsMinMaxCriteria = meetsBasicCriteria && 
                                  calculatedDeliveryAmount >= effectiveMinDeliveryAmountThreshold &&
                                  calculatedDeliveryAmount <= effectiveMaxDeliveryAmountThreshold;
        
        // Check if exceeds max criteria (delivery amount > max threshold)
        const exceedsMaxCriteria = meetsBasicCriteria && 
                                 calculatedDeliveryAmount > effectiveMaxDeliveryAmountThreshold;
        
        return {
          ...d,
        close_price: typeof d.close_price === 'number' ? d.close_price : null,
          traded_amount: calculatedTradedAmount,
          delivery_qty: typeof d.delivery_qty === 'number' ? d.delivery_qty : 0,
          delivery_amount: calculatedDeliveryAmount,
          avg_trade_worth: avgTradeWorth,
          meets_criteria: meetsMinMaxCriteria, 
          exceeds_max_criteria: exceedsMaxCriteria
        };
      })
      .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
      
    // Ensure we have at least 2 data points for curve drawing
    return validData.length >= 2 ? validData : [];
  }, [dailyData, deliveryThreshold, effectiveMinDeliveryAmountThreshold, effectiveMaxDeliveryAmountThreshold, screener]);

  // Add check if we have data to display
  if (sortedData.length === 0) {
    return (
      <div className={`w-full ${compact ? 'h-96' : 'h-[500px]'} rounded-2xl overflow-hidden border border-zinc-200 bg-white dark:border-zinc-800 dark:bg-zinc-900 p-2 flex items-center justify-center`}>
        <p className="text-zinc-500 dark:text-zinc-400">No data available</p>
      </div>
    );
  }

  // Calculate min and max values for chart axes, filtering out nulls
  const validPrices = sortedData
    .flatMap(d => [
      d.close_price,
      d.dma_200,
      typeof d.dma_50 === 'number' ? d.dma_50 : null,
      typeof d.dma_20 === 'number' ? d.dma_20 : null,
    ])
    .filter((v): v is number => typeof v === 'number' && Number.isFinite(v));

  const priceMin = validPrices.length > 0 ? Math.min(...validPrices) * 0.95 : 100;
  const priceMax = validPrices.length > 0 ? Math.max(...validPrices) * 1.05 : 200;
  
  // Calculate max for delivery_amount for y1 axis scaling
  const deliveryAmountMax = sortedData.length > 0 ? Math.max(...sortedData.map(d => d.delivery_amount || 0)) * 1.1 : 100;
  
  // Use delivery_amount for the y1 axis max
  const volumeAxisMax = deliveryAmountMax;
  
  // Get date labels from sorted data
  const dateLabels = sortedData.map(d => {
    // Ensure date is correctly parsed, handling either ISO format or YYYY-MM-DD
    const date = new Date(d.date);
    
    // Format date for display (compact or full)
    return compact 
      ? `${date.getDate()}/${date.getMonth() + 1}`
      : `${date.getDate()}/${date.getMonth() + 1}/${date.getFullYear().toString().slice(2)}`;
  });
  
  // Color scheme for chart
  const priceColor = chartTheme === 'dark' ? '#60a5fa' : '#3b82f6'; // Blue
  const dma200Color = chartTheme === 'dark' ? '#a3e635' : '#84cc16'; // Lime
  const dma50Color = chartTheme === 'dark' ? '#f472b6' : '#ec4899'; // Pink
  const dma20Color = chartTheme === 'dark' ? '#fde047' : '#eab308'; // Yellow

  // Which SMA lines to draw based on the active screener.
  const smaWindowsForScreener = SCREENER_SMA_WINDOWS[screener];
  const showDma20 = smaWindowsForScreener.includes('dma_20');
  const showDma50 = smaWindowsForScreener.includes('dma_50');
  const showDma200 = smaWindowsForScreener.includes('dma_200');
  const meetsMinMaxColor = 'rgba(250, 204, 21, 0.7)'; // Yellow for min-max criteria
  const exceedsMaxColor = 'rgba(139, 92, 246, 0.7)'; // Purple for exceeds max criteria
  const deliveryQtyColor = 'rgba(139, 92, 246, 0.6)'; // Purple for delivery qty
  const avgTradeWorthColor = chartTheme === 'dark' ? '#fb923c' : '#f97316'; // Orange for avg trade worth
  
  // Function to handle chart click to show detailed modal
  const handleChartClick = () => {
    if (isin && onChartClick) {
      onChartClick(isin);
    }
  };

  // Format large currency value for display
  const formatCurrency = (value: number) => {
    const isNegative = value < 0;
    const absValue = Math.abs(value);
    const sign = isNegative ? '-' : '';
    
    if (absValue >= 10000000) return `${sign}₹${(absValue / 10000000).toFixed(2)} Cr`;
    if (absValue >= 100000) return `${sign}₹${(absValue / 100000).toFixed(2)} L`;
    return `${sign}₹${absValue.toLocaleString('en-IN')}`;
  };

  // Format large number value for display using Lakh/Crore
  const formatAxisNumber = (value: number | string) => {
    const num = typeof value === 'string' ? parseFloat(value) : value;
    if (isNaN(num)) return 'N/A';
    const isNegative = num < 0;
    const absValue = Math.abs(num);
    const sign = isNegative ? '-' : '';
    
    if (absValue >= 10000000) return `${sign}${(absValue / 10000000).toFixed(1)} Cr`; // Use 1 decimal for axis
    if (absValue >= 100000) return `${sign}${(absValue / 100000).toFixed(1)} L`;     // Use 1 decimal for axis
    if (absValue >= 1000) return `${sign}${(absValue / 1000).toFixed(1)}K`;         // Use 1 decimal for axis
    return `${sign}${absValue.toFixed(0)}`; // No decimals for smaller numbers on axis
  };

  // Calculate average trade worth value range for scaling
  const validAvgTradeWorth = sortedData
    .map(d => d.avg_trade_worth)
    .filter((v): v is number => v !== undefined && v > 0);
  
  const avgTradeWorthMax = validAvgTradeWorth.length > 0 
    ? Math.max(...validAvgTradeWorth) * 1.1 
    : 100000;
  
  const chartData: ChartData = {
    labels: dateLabels,
    datasets: [
      // Volume as a bar chart
      {
        type: 'bar' as const,
        label: 'Delivery Amount',
        data: sortedData.map(d => {
          // Only include delivery amount for data points that meet min criteria or exceed max
          return (d.meets_criteria || d.exceeds_max_criteria) ? d.delivery_amount : null;
        }),
        backgroundColor: (context: ScriptableContext<'bar'>) => {
          // Color bars based on criteria: yellow for min-max range, purple for exceeding max
          const index = context.dataIndex;
          const dataPoint = sortedData[index];
          
          if (dataPoint?.exceeds_max_criteria) {
            return exceedsMaxColor; // Purple for exceeding max
          } else if (dataPoint?.meets_criteria) {
            return meetsMinMaxColor; // Yellow for meeting min-max criteria
          } else {
            return 'rgba(0,0,0,0)'; // Transparent for bars that don't meet criteria
          }
        },
        borderColor: 'transparent',
        borderWidth: 0,
        borderRadius: 3,
        borderSkipped: false,
        yAxisID: 'y1',
        barPercentage: barPercentage,
        categoryPercentage: 0.8,
        order: 4,
        datalabels: {
          display: false
        }
      },
      // Delivery quantity
      {
        type: 'bar' as const,
        label: 'Delivery Qty',
        data: sortedData.map(d => d.delivery_qty),
        backgroundColor: deliveryQtyColor,
        borderColor: 'transparent',
        borderWidth: 0,
        borderRadius: 3,
        borderSkipped: false,
        yAxisID: 'y1',
        barPercentage: 0.4,
        categoryPercentage: 0.4,
        order: 3,
        datalabels: {
          display: false
        }
      },
      // Price line
      {
        type: 'line' as const,
        label: 'Close Price',
        data: sortedData.map(d => d.close_price),
        borderColor: priceColor,
        backgroundColor: priceColor,
        pointBackgroundColor: priceColor,
        pointBorderColor: '#fff',
        pointHoverBackgroundColor: '#fff',
        pointHoverBorderColor: priceColor,
        pointRadius: (ctx: ScriptableContext<'line'>) => sortedData[ctx.dataIndex]?.meets_criteria ? 4 : 0, // Highlight points meeting criteria
        pointHoverRadius: 5,
        borderWidth: 2,
        yAxisID: 'y',
        tension: 0,
        spanGaps: true // Add this to handle null values
      },
      // 200-Day Moving Average
      ...(showDma200
        ? [{
            type: 'line' as const,
            label: '200-Day MA',
            data: sortedData.map(d => d.dma_200),
            borderColor: dma200Color,
            borderWidth: 1.5,
            pointRadius: 0,
            yAxisID: 'y',
            tension: 0,
            spanGaps: true,
          }]
        : []),
      // 50-Day Moving Average (drawn only for screeners that gate on SMA50)
      ...(showDma50
        ? [{
            type: 'line' as const,
            label: '50-Day MA',
            data: sortedData.map(d => (typeof d.dma_50 === 'number' ? d.dma_50 : null)),
            borderColor: dma50Color,
            borderWidth: 1.5,
            borderDash: [6, 4],
            pointRadius: 0,
            yAxisID: 'y',
            tension: 0,
            spanGaps: true,
          }]
        : []),
      // 20-Day Moving Average (drawn only for screeners that gate on SMA20)
      ...(showDma20
        ? [{
            type: 'line' as const,
            label: '20-Day MA',
            data: sortedData.map(d => (typeof d.dma_20 === 'number' ? d.dma_20 : null)),
            borderColor: dma20Color,
            borderWidth: 1.5,
            borderDash: [2, 3],
            pointRadius: 0,
            yAxisID: 'y',
            tension: 0,
            spanGaps: true,
          }]
        : []),
      // Average Trade Worth line
      {
        type: 'line' as const,
        label: 'Avg Trade Worth',
        data: sortedData.map(d => d.avg_trade_worth || null),
        borderColor: avgTradeWorthColor,
        backgroundColor: avgTradeWorthColor,
        pointBackgroundColor: avgTradeWorthColor,
        pointBorderColor: '#fff',
        pointHoverBackgroundColor: '#fff',
        pointHoverBorderColor: avgTradeWorthColor,
        pointRadius: 0,
        pointHoverRadius: 4,
        borderWidth: 1.5,
        borderDash: [4, 4],
        yAxisID: 'y3',
        tension: 0,
        spanGaps: true // Skip null values
      },
    ],
  };

  const chartOptions: ChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    animation: false,
    layout: {
      padding: {
        left: 10,
        right: 15,
        top: 20,
        bottom: 10,
      },
    },
    onClick: (event, elements, chart) => {
      if (isin && onChartClick) {
        onChartClick(isin);
      }
    },
    interaction: {
      mode: 'index',
      intersect: false,
      axis: 'x'
    },
    elements: {
      line: {
        tension: 0,
      },
      point: {
        radius: 0, // Hide points by default
      }
    },
    scales: {
      // X axis (dates)
      x: {
        grid: {
          display: false,
          color: chartTheme === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)',
        },
        ticks: {
          color: chartTheme === 'dark' ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.7)',
          maxRotation: 45,
          minRotation: 45,
          font: {
            size: 10,
          },
          // Show fewer labels on small screens
          maxTicksLimit: compact ? 8 : 12,
        },
        border: {
          color: chartTheme === 'dark' ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.2)',
        },
      },
      // Price axis (left)
      y: {
        type: 'linear',
        position: 'left',
        grid: {
          color: chartTheme === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)',
        },
        ticks: {
          color: chartTheme === 'dark' ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.7)',
          font: {
            size: 10,
          },
          callback: function(value) {
            return '₹' + Number(value).toFixed(2);
          },
          maxTicksLimit: 8,
        },
        min: priceMin, // Ensure all price data fits on chart
        max: priceMax,
        beginAtZero: false,
        border: {
          color: chartTheme === 'dark' ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.2)',
        },
        title: {
          display: !compact,
          text: 'Price (₹)',
          color: chartTheme === 'dark' ? 'rgba(255, 255, 255, 0.8)' : 'rgba(0, 0, 0, 0.8)',
          font: {
            size: 11,
            weight: 'bold'
          },
        }
      },
      // Volume & Delivery Qty axis (right)
      y1: {
        type: 'linear',
        position: 'right',
        grid: {
          drawOnChartArea: false, // only draw grid lines for price axis
        },
        ticks: {
          color: chartTheme === 'dark' ? 'rgba(255, 255, 255, 0.5)' : 'rgba(0, 0, 0, 0.5)',
          font: {
            size: 10,
          },
          callback: function(value) {
            const numValue = Number(value); // Convert to number
            if (isNaN(numValue)) return value; // Return original if not a number
            const isNegative = numValue < 0;
            const absValue = Math.abs(numValue);
            const sign = isNegative ? '-' : '';
            
            if (absValue >= 10000000) return `${sign}₹${(absValue / 10000000).toFixed(2)} Cr`;
            if (absValue >= 100000) return `${sign}₹${(absValue / 100000).toFixed(2)} L`;
            return `${sign}₹${absValue.toLocaleString('en-IN')}`; // Use the number for toLocaleString
          },
          maxTicksLimit: 6,
        },
        min: 0, // Volume/Qty starts at 0
        max: volumeAxisMax, // Use calculated max
        beginAtZero: true,
        border: {
          color: chartTheme === 'dark' ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.2)',
        },
        title: {
          display: !compact,
          text: 'Delivery Amount',
          color: chartTheme === 'dark' ? '#9ca3af' : '#4b5563',
          font: { size: 12 },
          padding: { top: 0, bottom: 5 }
        },
      },
      // Average Trade Worth axis
      y3: {
        type: 'linear',
        position: 'right',
        grid: {
          drawOnChartArea: false,
        },
        ticks: {
          color: chartTheme === 'dark' ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.7)',
          font: {
            size: 10,
          },
          callback: function(value) {
            const numValue = Number(value);
            const isNegative = numValue < 0;
            const absValue = Math.abs(numValue);
            const sign = isNegative ? '-' : '';
            
            if (absValue >= 100000) return `${sign}₹${(absValue / 100000).toFixed(1)}L`;
            if (absValue >= 1000) return `${sign}₹${(absValue / 1000).toFixed(1)}K`;
            return `${sign}₹${absValue.toFixed(0)}`;
          },
          maxTicksLimit: 5,
        },
        min: 0,
        max: avgTradeWorthMax,
        beginAtZero: true,
        display: validAvgTradeWorth.length > 0,
        border: {
          color: chartTheme === 'dark' ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.2)',
        },
        title: {
          display: !compact && validAvgTradeWorth.length > 0,
          text: 'Avg Trade Worth (₹)',
          color: chartTheme === 'dark' ? 'rgba(255, 255, 255, 0.8)' : 'rgba(0, 0, 0, 0.8)',
          font: {
            size: 11,
            weight: 'bold'
          },
        }
      }
    },
    plugins: {
      annotation: {
        annotations: {},
      },
      datalabels: {
        display: false,
        font: {
          weight: 'bold' as const,
          size: 9
        },
        color: 'white',
        backgroundColor: 'rgba(0, 0, 0, 0.6)',
        borderRadius: 4,
        padding: 3
      },
      legend: {
        display: true,
        position: 'top',
        align: 'center',
        labels: {
          color: chartTheme === 'dark' ? 'rgba(255, 255, 255, 0.9)' : 'rgba(0, 0, 0, 0.9)',
          boxWidth: 12,
          boxHeight: 12,
          padding: 15,
          font: {
            size: 11,
          },
          usePointStyle: true,
          pointStyle: 'rectRounded',
          filter: function(item) {
            // Don't show criteria bars in legend for compact mode or when no criteria are met
            if ((compact || !sortedData.some(d => d.meets_criteria)) && 
                (item.text.includes('Criteria:'))) {
              return false;
            }
            // Hide labels for Delivery Qty
            if (item.text === 'Delivery Qty') {
              return false;
            }
            return true;
          },
        },
      },
      title: {
        display: true,
        text: `${stockName} - Stock Analysis`,
        color: chartTheme === 'dark' ? 'rgba(255, 255, 255, 0.95)' : 'rgba(0, 0, 0, 0.95)',
        font: {
          size: compact ? 14 : 16,
          weight: 'bold',
        },
        padding: {
          top: 10,
          bottom: 15,
        },
      },
      subtitle: {
        display: !compact,
        text: 'Price Analysis',
        color: chartTheme === 'dark' ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.7)',
        font: {
          size: 12,
          style: 'italic',
        },
        padding: {
          bottom: 10,
        }
      },
      tooltip: {
        enabled: showTooltip,
        position: 'nearest',
        backgroundColor: chartTheme === 'dark' ? 'rgba(17, 24, 39, 0.95)' : 'rgba(255, 255, 255, 0.98)',
        titleColor: chartTheme === 'dark' ? '#e2e8f0' : '#0f172a',
        bodyColor: chartTheme === 'dark' ? '#cbd5e1' : '#1e293b',
        footerColor: chartTheme === 'dark' ? '#e2e8f0' : '#0f172a',
        borderColor: chartTheme === 'dark' ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.15)',
        borderWidth: 2,
        padding: 14,
        caretPadding: 25,
        cornerRadius: 8,
        boxPadding: 6,
        yAlign: 'bottom', 
        xAlign: 'center',
        titleFont: {
          size: 14,
          weight: 'bold' as const
        },
        bodyFont: {
          size: 12,
          weight: 500
        },
        titleAlign: 'center',
        bodyAlign: 'left',
        displayColors: true,
        boxWidth: 8,
        boxHeight: 8,
        usePointStyle: true,
        callbacks: {
          title: (items: TooltipItem<'bar' | 'line'>[]) => {
            if (items.length > 0) {
              const index = items[0].dataIndex;
              const dataPoint = sortedData[index];
              if (dataPoint) {
                // Ensure date is correctly parsed
                const dateObj = new Date(dataPoint.date);
                // Format date for tooltip
                return dateObj.toLocaleDateString('en-US', { 
                  weekday: 'long',
                  year: 'numeric', 
                  month: 'long', 
                  day: 'numeric' 
                });
              }
            }
            return '';
          },
          label: (context: TooltipItem<'bar' | 'line'>) => {
            let label = context.dataset.label || '';
            if (label) {
              label += ': ';
            }
            if (context.parsed.y !== null) {
              const value = context.parsed.y;
              // Format based on dataset
              if (context.dataset.yAxisID === 'y') { // Price axis
                label += `₹${value.toFixed(2)}`;
              } else if (context.dataset.yAxisID === 'y1' && context.dataset.label?.includes('Delivery Amount')) { // Amount Axis
                // Use the refined currency formatter for Delivery Amount
                label += formatCurrency(value);
              } else if (context.dataset.yAxisID === 'y3') { // Avg Trade Worth Axis (changed from y2)
                label += formatCurrency(value); // Use formatCurrency for Avg Trade Worth
              } else {
                label += value.toFixed(2); // Default formatting
              }
            }
            return label;
          },
          footer: (tooltipItems: TooltipItem<'line' | 'bar'>[]) => {
            const index = tooltipItems[0]?.dataIndex;
            if (index !== undefined && sortedData[index]) {
              const dataPoint = sortedData[index];
              const deliveryPct = (dataPoint.delivery_qty / dataPoint.traded_qty) * 100;
              const lines = [];
              // Use formatAxisNumber for large quantities like Delivery Qty
              lines.push(`Delivery: ${formatAxisNumber(dataPoint.delivery_qty)} (${deliveryPct.toFixed(1)}%)`);
              lines.push(`Delivery Amt: ${formatCurrency(dataPoint.delivery_amount)}`);
              lines.push(`Traded Amt: ${formatCurrency(dataPoint.traded_amount)}`);
              if (dataPoint.number_of_trades) {
                lines.push(`Trades: ${dataPoint.number_of_trades}`);
              }
              if (dataPoint.avg_trade_worth) {
                lines.push(`Avg Trade Worth: ${formatCurrency(dataPoint.avg_trade_worth)}`);
              }
              if (dataPoint.meets_criteria) {
                lines.push('Status: Meets Criteria');
              } else if (dataPoint.exceeds_max_criteria) {
                lines.push('Status: Exceeds Max Criteria');
              }
              return lines;
            }
            return [];
          }
        }
      },
    }
  };

  return (
    <div className="space-y-2">
      <div 
        className={`w-full ${compact ? 'h-80' : 'h-[420px]'} relative rounded-2xl overflow-hidden bg-white dark:bg-zinc-900 p-4 border border-zinc-200 dark:border-zinc-800 shadow-sm ${isin && onChartClick ? 'cursor-pointer' : ''}`}
        onClick={handleChartClick}
      >
        <Chart id="stock-chart" type='bar' data={chartData} options={chartOptions} />
      </div>
    </div>
  );
}